import React, { useMemo, useState } from 'react';
import { View, TouchableOpacity, StyleSheet, ScrollView, Platform } from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { useSafeAreaInsets, initialWindowMetrics } from 'react-native-safe-area-context';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { FileUploadSection, UploadFile, FileUploadThemeColors } from '@/components/common/admin/mobile/FileUploadSection';
import { useCreateConversationMutation } from '@/store/api/admin/messagesApi';
import { useAppSelector } from '@/store';
import { buildMessageFormData } from '@/services/residents/messages/formData';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useResponsive } from '@/hooks/common/useResponsive';
import { ChatGlassEmptyState } from '@/components/residents/redesign/chat';
import { useChatAttachments } from '@/hooks/residents/useChatAttachments';
import { showPhotoPickerActionSheet } from '@/utils/common/alert';
import AttachmentPreview from '@/components/residents/propertyChat/mobile/AttachmentPreview';
import { logger } from '@/utils/common/logger';

type Props = {
  onClose: () => void;
  onCreated?: (conversationId: number, subject?: string) => void;
  initialSubject?: string;
};

function useDesktopStyles() {
  return useThemedStyles(({ colors, semantic, components }) =>
    StyleSheet.create({
      container: {
        flex: 1,
        backgroundColor: colors.bg.base,
      },
      scroll: { flex: 1 },
      content: { padding: 16, paddingBottom: 24 },
      title: { fontSize: 18, fontWeight: '600', color: colors.text.primary, marginBottom: 12 },
      fieldSection: { marginBottom: 12 },
      label: { fontSize: 14, color: semantic.interactive.primaryHover, marginBottom: 6 },
      input: {
        backgroundColor: colors.bg.gradientStart,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.card.border,
        paddingHorizontal: 12,
        paddingVertical: 10,
        color: colors.text.primary,
      },
      compose: { marginTop: 12 },
      composeTitle: { fontSize: 16, color: colors.text.primary, marginBottom: 8, fontWeight: '600' },
      composeInput: {
        minHeight: 140,
        backgroundColor: colors.bg.gradientStart,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: colors.card.border,
        paddingHorizontal: 12,
        paddingVertical: 10,
        color: colors.text.primary,
      },
      footer: {
        padding: 12,
        borderTopWidth: 1,
        borderTopColor: colors.card.border,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
      },
      cancel: {
        paddingHorizontal: 12,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: colors.bg.gradientStart,
      },
      cancelText: { color: colors.text.primary, fontWeight: '600' },
      send: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderRadius: 8,
        backgroundColor: semantic.interactive.primary,
      },
      sendDisabled: { opacity: 0.6 },
      sendText: { color: components.button.primaryText, fontWeight: '700' },
      placeholderColor: { color: colors.text.subtle },
    }),
  );
}

function useFileUploadThemeColors(): FileUploadThemeColors {
  const { colors, semantic } = useThemeColors();
  return {
    surface: colors.glass.buttonBg,
    border: colors.card.border,
    primary: semantic.interactive.primary,
    textPrimary: colors.text.primary,
    textSecondary: colors.text.secondary,
    textTertiary: colors.text.muted,
    iconBg: colors.bg.gradientStart,
    error: semantic.status.error,
  };
}

export const ResidentCreateConversation: React.FC<Props> = ({ onClose, onCreated, initialSubject }) => {
  const { colors } = useThemeColors();
  const fileUploadColors = useFileUploadThemeColors();
  const styles = useDesktopStyles();
  const subjectHover = useHoverStyle('field');
  const messageHover = useHoverStyle('field');
  const [subject, setSubject] = useState(initialSubject || '');
  const [messageContent, setMessageContent] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  // files (web desktop) retained for web UI
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [createConversation, { isLoading }] = useCreateConversationMutation();
  const propertyId = useAppSelector((s) => s.auth.user?.propertyId);
  const { layoutMode } = useResponsive();
  const insets = useSafeAreaInsets();
  const isWeb = Platform.OS === 'web';
  const isDesktopLayout = layoutMode === 'desktop';

  // Mobile attachments using the chat attachments hook
  const { attachments, addFromCamera, addFromLibrary, removeAttachment, resetAttachments } = useChatAttachments();

  const disableSend = useMemo(() => {
    if (isLoading) return true;
    // Both desktop and mobile: require subject AND message body
    return !subject.trim() || !messageContent.trim();
  }, [isLoading, subject, messageContent]);

  const handleSubmit = async () => {
    if (!propertyId) return;
    if (disableSend) return;
    // For create conversation, backend expects 'messageContent' (not 'content')
    // Use web files for desktop, mobile attachments for native mobile
    const filesToUpload = isWeb ? files : attachments;
    const formData = buildMessageFormData({
      isAdmin: false,
      files: filesToUpload as any,
      extras: {
        subject: subject.trim(),
        messageContent: messageContent.trim(),
        sendEmail: String(sendEmail),
        messageType: 'groupChat',
        propertyId: String(propertyId),
      },
    });
    try {
      const resp = await createConversation(formData).unwrap();
      if (resp?.success && resp.response?.id) {
        resetAttachments();
        if (onCreated) {
          onCreated(resp.response.id, subject.trim());
        } else {
          onClose();
        }
      } else if (resp?.success) {
        resetAttachments();
        onClose();
      }
    } catch (e) {
      logger.warn('conversationCreateFailed', e);
    }
  };

  const placeholderColor = (styles.placeholderColor as any).color;

  if (isDesktopLayout) {
    // Desktop: return desktop layout (responsive to viewport size)
    return (
      <View style={styles.container}>
        <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
          <Text style={styles.title}>New conversation</Text>
          <View style={styles.fieldSection}>
            <Text style={styles.label}>Subject</Text>
            <TextInput
              style={[styles.input, subjectHover.viewStyle]}
              {...(subjectHover.mouseProps as any)}
              value={subject}
              onChangeText={setSubject}
              placeholder="Enter message subject"
              placeholderTextColor={placeholderColor}
              maxLength={100}
            />
          </View>
          <FileUploadSection
            title="Attachments"
            subtitle="Add files to your message (optional)"
            files={files}
            onFilesChange={setFiles}
            maxFiles={5}
            themeColors={fileUploadColors}
          />
          <View style={styles.compose}>
            <Text style={styles.composeTitle}>{subject || 'Untitled message'}</Text>
            <TextInput
              style={[styles.composeInput, messageHover.viewStyle]}
              {...(messageHover.mouseProps as any)}
              value={messageContent}
              onChangeText={setMessageContent}
              placeholder="Your message..."
              placeholderTextColor={placeholderColor}
              multiline
            />
          </View>
        </ScrollView>
        <View style={styles.footer}>
          <Hoverable hover="button" style={styles.cancel} onPress={onClose}>
            <Text style={styles.cancelText}>Cancel</Text>
          </Hoverable>
          <Hoverable
            hover="button"
            style={[styles.send, disableSend && styles.sendDisabled]}
            onPress={handleSubmit}
            disabled={disableSend}
          >
            <Text style={styles.sendText}>Start Chat</Text>
          </Hoverable>
        </View>
      </View>
    );
  }

  // Mobile layout matching Figma design
  // Use different SVG icons based on button state (icons have background baked in)
  const SendIconActive = RESIDENTS_SVG_COMPONENTS.CHAT_SEND_ACTIVE_BUTTON;
  const SendIconInactive = RESIDENTS_SVG_COMPONENTS.CHAT_SEND_INACTIVE_BUTTON;
  const AttachIcon = RESIDENTS_SVG_COMPONENTS.PAPERCLIP;

  return (
    <View style={mobileStyles.container}>
      <ScrollView
        style={mobileStyles.contentArea}
        contentContainerStyle={mobileStyles.contentAreaContainer}
        keyboardShouldPersistTaps="handled"
      >
        <ChatGlassEmptyState type="startConversation" />
      </ScrollView>

      <View style={[mobileStyles.bottomSection, { paddingBottom: Math.max(insets.bottom, initialWindowMetrics?.insets?.bottom ?? 0, 24) }]}>
        <View style={[mobileStyles.inputContainer, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
          {subject.length > 0 && (
            <Text style={[mobileStyles.floatingLabel, { color: colors.text.subtle }]}>Subject</Text>
          )}
          <TextInput
            style={[
              mobileStyles.input,
              { color: colors.text.primary },
              subject.length > 0 && mobileStyles.inputWithLabel,
            ]}
            value={subject}
            onChangeText={setSubject}
            placeholder="Subject"
            placeholderTextColor={colors.text.subtle}
            maxLength={100}
            returnKeyType="next"
          />
        </View>

        {attachments.length > 0 && (
          <AttachmentPreview attachments={attachments as any} onRemove={removeAttachment} />
        )}

        <View style={[mobileStyles.messageRow, { borderTopColor: colors.separator }]}>
          <TouchableOpacity
            style={mobileStyles.attachButton}
            onPress={() => showPhotoPickerActionSheet(addFromLibrary, addFromCamera)}
          >
            <AttachIcon width={24} height={24} />
          </TouchableOpacity>
          <View style={mobileStyles.messageInputContainer}>
            <TextInput
              style={[mobileStyles.messageInput, { color: colors.text.primary }]}
              value={messageContent}
              onChangeText={setMessageContent}
              placeholder="Your message..."
              placeholderTextColor={colors.text.subtle}
              multiline
            />
          </View>
          <TouchableOpacity
            style={mobileStyles.sendButton}
            onPress={handleSubmit}
            disabled={disableSend}
          >
            {disableSend ? (
              <SendIconInactive width={34} height={34} />
            ) : (
              <SendIconActive width={34} height={34} />
            )}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default ResidentCreateConversation;

const mobileStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentArea: {
    flex: 1,
  },
  contentAreaContainer: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  bottomSection: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    gap: 20,
  },
  inputContainer: {
    borderWidth: 1,
    borderRadius: 12,
    minHeight: 40,
    paddingVertical: 4,
    paddingHorizontal: 16,
    justifyContent: 'center',
  },
  floatingLabel: {
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 12,
    marginBottom: 2,
  },
  input: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 17.5,
    padding: 0,
  },
  inputWithLabel: {},
  messageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    borderTopWidth: 1,
    paddingTop: 20,
    marginHorizontal: -REDESIGN_SPACING.horizontalPadding,
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
  },
  messageInputContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 6,
  },
  messageInput: {
    ...REDESIGN_TYPOGRAPHY.body,
    padding: 0,
  },
  attachButton: {
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButton: {
    width: 34,
    height: 34,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
